import apiClient from './apiClient';

const API_URL = '/caratula';

/**
 * Mensaje legible de un error al generar. Los POST de generación piden `responseType: 'blob'`,
 * así que el ApiError del backend ({ code, message }) llega como Blob y hay que leerlo como
 * texto: si no, el 400 de validación ("MAIL: ... no es un correo válido") nunca se muestra.
 */
export const extraerMensajeError = async (error, porDefecto = 'No se pudo generar la Carátula. Intente nuevamente.') => {
    const data = error?.response?.data;
    try {
        if (data instanceof Blob) {
            const texto = await data.text();
            const json = JSON.parse(texto);
            return json?.message || porDefecto;
        }
        if (data?.message) {
            return data.message;
        }
    } catch (e) {
        // Cuerpo que no es JSON (HTML de un proxy, vacío): cae al genérico.
    }
    return porDefecto;
};

// Prefill del modal: un valor por renglón de la carátula, trazado desde el trámite/cliente.
// Lo que falta viaja vacío para completar a mano.
export const obtenerDatosCaratulaRti = async (idTramite) => {
    try {
        const response = await apiClient.get(`${API_URL}/rti/tramite/${idTramite}/datos`);
        return response.data;
    } catch (error) {
        console.error('❌ Error obteniendo los datos de la Carátula RTI:', error);
        throw error;
    }
};

// Genera la carátula con los datos confirmados en el modal (editados o tal cual vinieron
// del prefill): se descarga y además queda adjuntada a los Archivos del trámite.
export const generarCaratulaRtiTramite = async (idTramite, datos) => {
    try {
        const response = await apiClient.post(`${API_URL}/rti/tramite/${idTramite}`, datos, {
            responseType: 'blob',
        });
        return response;
    } catch (error) {
        console.error('❌ Error generando la Carátula RTI:', error);
        throw error;
    }
};

// ---------------------------------------------------------------------------
// ART — mismo par de llamadas que RTI, contra los endpoints /caratula/art/...
// ---------------------------------------------------------------------------

export const obtenerDatosCaratulaArt = async (idTramite) => {
    try {
        const response = await apiClient.get(`${API_URL}/art/tramite/${idTramite}/datos`);
        return response.data;
    } catch (error) {
        console.error("❌ Error obteniendo los datos de la Carátula ART:", error);
        throw error;
    }
};

export const generarCaratulaArtTramite = async (idTramite, datos) => {
    try {
        const response = await apiClient.post(`${API_URL}/art/tramite/${idTramite}`, datos, {
            responseType: "blob",
        });
        return response;
    } catch (error) {
        console.error("❌ Error generando la Carátula ART:", error);
        throw error;
    }
};

// ---------------------------------------------------------------------------
// Reajuste — mismo par de llamadas, contra los endpoints /caratula/reajuste/...
//
// La diferencia con RTI/ART es que el prefill trae además `variante` (BADARO o
// REPARACION_HISTORICA, derivada del tipoReajuste del trámite): el modal la usa para saber
// qué renglones mostrar y el backend para elegir cuál de las dos plantillas emite.
// ---------------------------------------------------------------------------

export const obtenerDatosCaratulaReajuste = async (idTramite, variante) => {
    try {
        const response = await apiClient.get(`${API_URL}/reajuste/tramite/${idTramite}/datos`, {
            params: { variante },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error obteniendo los datos de la Carátula de Reajuste:', error);
        throw error;
    }
};

export const generarCaratulaReajusteTramite = async (idTramite, datos) => {
    try {
        const response = await apiClient.post(`${API_URL}/reajuste/tramite/${idTramite}`, datos, {
            responseType: 'blob',
        });
        return response;
    } catch (error) {
        console.error('❌ Error generando la Carátula de Reajuste:', error);
        throw error;
    }
};

// ---------------------------------------------------------------------------
// PNC — mismo par de llamadas que RTI/ART, contra los endpoints /caratula/pnc/...
// ---------------------------------------------------------------------------

export const obtenerDatosCaratulaPnc = async (idTramite) => {
    try {
        const response = await apiClient.get(`${API_URL}/pnc/tramite/${idTramite}/datos`);
        return response.data;
    } catch (error) {
        console.error('❌ Error obteniendo los datos de la Carátula PNC:', error);
        throw error;
    }
};

export const generarCaratulaPncTramite = async (idTramite, datos) => {
    try {
        const response = await apiClient.post(`${API_URL}/pnc/tramite/${idTramite}`, datos, {
            responseType: 'blob',
        });
        return response;
    } catch (error) {
        console.error('❌ Error generando la Carátula PNC:', error);
        throw error;
    }
};

// ---------------------------------------------------------------------------
// Amparo PNC — mismo par de llamadas, contra los endpoints /caratula/amparo-pnc/...
// ---------------------------------------------------------------------------

export const obtenerDatosCaratulaAmparoPnc = async (idTramite) => {
    try {
        const response = await apiClient.get(`${API_URL}/amparo-pnc/tramite/${idTramite}/datos`);
        return response.data;
    } catch (error) {
        console.error('❌ Error obteniendo los datos de la Carátula de Amparo PNC:', error);
        throw error;
    }
};

export const generarCaratulaAmparoPncTramite = async (idTramite, datos) => {
    try {
        const response = await apiClient.post(`${API_URL}/amparo-pnc/tramite/${idTramite}`, datos, {
            responseType: 'blob',
        });
        return response;
    } catch (error) {
        console.error('❌ Error generando la Carátula de Amparo PNC:', error);
        throw error;
    }
};

// ---------------------------------------------------------------------------
// Jubilación — dos plantillas (LEY_24476 / LEY_27675) y la elige el operador desde el menú
// del botón, así que la variante viaja como query param en el prefill y dentro del DTO al
// generar. En Reajuste, en cambio, la deriva el backend y el front no la manda.
// ---------------------------------------------------------------------------

export const obtenerDatosCaratulaJubilacion = async (idTramite, variante) => {
    try {
        const response = await apiClient.get(`${API_URL}/jubilacion/tramite/${idTramite}/datos`, {
            params: { variante },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error obteniendo los datos de la Carátula de Jubilación:', error);
        throw error;
    }
};

export const generarCaratulaJubilacionTramite = async (idTramite, datos) => {
    try {
        const response = await apiClient.post(`${API_URL}/jubilacion/tramite/${idTramite}`, datos, {
            responseType: 'blob',
        });
        return response;
    } catch (error) {
        console.error('❌ Error generando la Carátula de Jubilación:', error);
        throw error;
    }
};

// ---------------------------------------------------------------------------
// Plan de Pago — mismo par de llamadas, contra los endpoints /caratula/plan-pago/...
// ---------------------------------------------------------------------------

export const obtenerDatosCaratulaPlanPago = async (idTramite) => {
    try {
        const response = await apiClient.get(`${API_URL}/plan-pago/tramite/${idTramite}/datos`);
        return response.data;
    } catch (error) {
        console.error('❌ Error obteniendo los datos de la Carátula de Plan de Pago:', error);
        throw error;
    }
};

export const generarCaratulaPlanPagoTramite = async (idTramite, datos) => {
    try {
        const response = await apiClient.post(`${API_URL}/plan-pago/tramite/${idTramite}`, datos, {
            responseType: 'blob',
        });
        return response;
    } catch (error) {
        console.error('❌ Error generando la Carátula de Plan de Pago:', error);
        throw error;
    }
};

// ---------------------------------------------------------------------------
// PUAM — mismo par de llamadas, contra los endpoints /caratula/puam/...
// ---------------------------------------------------------------------------

export const obtenerDatosCaratulaPuam = async (idTramite) => {
    try {
        const response = await apiClient.get(`${API_URL}/puam/tramite/${idTramite}/datos`);
        return response.data;
    } catch (error) {
        console.error('❌ Error obteniendo los datos de la Carátula de PUAM:', error);
        throw error;
    }
};

export const generarCaratulaPuamTramite = async (idTramite, datos) => {
    try {
        const response = await apiClient.post(`${API_URL}/puam/tramite/${idTramite}`, datos, {
            responseType: 'blob',
        });
        return response;
    } catch (error) {
        console.error('❌ Error generando la Carátula de PUAM:', error);
        throw error;
    }
};

// ---------------------------------------------------------------------------
// Reconocimiento de Servicios — mismo par de llamadas, contra /caratula/reconocimiento/...
// Lo que el reconocimiento no tiene cargado (cuota, cuenta AFIP, planes, aportes) lo trae
// el backend del trámite padre (Jubilación / Plan de Pago).
// ---------------------------------------------------------------------------

export const obtenerDatosCaratulaReconocimiento = async (idTramite) => {
    try {
        const response = await apiClient.get(`${API_URL}/reconocimiento/tramite/${idTramite}/datos`);
        return response.data;
    } catch (error) {
        console.error('❌ Error obteniendo los datos de la Carátula de Reconocimiento de Servicios:', error);
        throw error;
    }
};

export const generarCaratulaReconocimientoTramite = async (idTramite, datos) => {
    try {
        const response = await apiClient.post(`${API_URL}/reconocimiento/tramite/${idTramite}`, datos, {
            responseType: 'blob',
        });
        return response;
    } catch (error) {
        console.error('❌ Error generando la Carátula de Reconocimiento de Servicios:', error);
        throw error;
    }
};

// ---------------------------------------------------------------------------
// Pensión — idTramite es un trámite de Pensión o, para los viejos, un GENERICO ("Otro
// Trámite" de Recepción). Tres plantillas (DIRECTA / DERIVADA / HIJO_DISCAPACIDAD) elegidas
// por el operador, igual que Jubilación: la variante viaja como query param en el prefill y
// dentro del DTO al generar.
// ---------------------------------------------------------------------------

export const obtenerDatosCaratulaPension = async (idTramite, variante) => {
    try {
        const response = await apiClient.get(`${API_URL}/pension/tramite/${idTramite}/datos`, {
            params: { variante },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error obteniendo los datos de la Carátula de Pensión:', error);
        throw error;
    }
};

export const generarCaratulaPensionTramite = async (idTramite, datos) => {
    try {
        const response = await apiClient.post(`${API_URL}/pension/tramite/${idTramite}`, datos, {
            responseType: 'blob',
        });
        return response;
    } catch (error) {
        console.error('❌ Error generando la Carátula de Pensión:', error);
        throw error;
    }
};

// ---------------------------------------------------------------------------
// Reajuste de Pensión — misma situación que Pensión (cuelga de un trámite GENERICO). Dos
// plantillas (BADARO / REPARACION_HISTORICA) elegidas por el operador.
// ---------------------------------------------------------------------------

export const obtenerDatosCaratulaReajustePension = async (idTramite, variante) => {
    try {
        const response = await apiClient.get(`${API_URL}/reajuste-pension/tramite/${idTramite}/datos`, {
            params: { variante },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error obteniendo los datos de la Carátula de Reajuste de Pensión:', error);
        throw error;
    }
};

export const generarCaratulaReajustePensionTramite = async (idTramite, datos) => {
    try {
        const response = await apiClient.post(`${API_URL}/reajuste-pension/tramite/${idTramite}`, datos, {
            responseType: 'blob',
        });
        return response;
    } catch (error) {
        console.error('❌ Error generando la Carátula de Reajuste de Pensión:', error);
        throw error;
    }
};

// ---------------------------------------------------------------------------
// Declaratoria de Herederos — mismo par de llamadas, contra los endpoints
// /caratula/declaratoria/... Primera carátula de la serie con grilla (hasta 4 herederos).
// ---------------------------------------------------------------------------

export const obtenerDatosCaratulaDeclaratoria = async (idTramite) => {
    try {
        const response = await apiClient.get(`${API_URL}/declaratoria/tramite/${idTramite}/datos`);
        return response.data;
    } catch (error) {
        console.error('❌ Error obteniendo los datos de la Carátula de Declaratoria:', error);
        throw error;
    }
};

export const generarCaratulaDeclaratoriaTramite = async (idTramite, datos) => {
    try {
        const response = await apiClient.post(`${API_URL}/declaratoria/tramite/${idTramite}`, datos, {
            responseType: 'blob',
        });
        return response;
    } catch (error) {
        console.error('❌ Error generando la Carátula de Declaratoria:', error);
        throw error;
    }
};

// ---------------------------------------------------------------------------
// Impuesto a las Ganancias — mismo par de llamadas, contra los endpoints
// /caratula/ganancias/... ("RECLAMO ART 9 LEY 24463").
// ---------------------------------------------------------------------------

export const obtenerDatosCaratulaGanancias = async (idTramite) => {
    try {
        const response = await apiClient.get(`${API_URL}/ganancias/tramite/${idTramite}/datos`);
        return response.data;
    } catch (error) {
        console.error('❌ Error obteniendo los datos de la Carátula de Ganancias:', error);
        throw error;
    }
};

export const generarCaratulaGananciasTramite = async (idTramite, datos) => {
    try {
        const response = await apiClient.post(`${API_URL}/ganancias/tramite/${idTramite}`, datos, {
            responseType: 'blob',
        });
        return response;
    } catch (error) {
        console.error('❌ Error generando la Carátula de Ganancias:', error);
        throw error;
    }
};

// ---------------------------------------------------------------------------
// Generación INDEPENDIENTE (página "Generar Documentos Automáticos") — sin trámite. `cuil`
// es opcional: sin él el prefill vuelve todo en blanco. Dos funciones genéricas en vez de un
// par por módulo: cada carátula ya trae su `urlBase` en caratulaRegistry.js (el mismo
// segmento que usan las funciones de arriba, ej. 'rti', 'amparo-pnc', 'reajuste-pension'), así
// que alcanza con parametrizar la URL. El documento se descarga: no se adjunta a nada porque
// no hay trámite del que colgar.
// ---------------------------------------------------------------------------

export const obtenerDatosCaratulaIndependiente = async (urlBase, cuil, variante) => {
    try {
        const response = await apiClient.get(`${API_URL}/${urlBase}/cliente/datos`, {
            params: { cuil: cuil || undefined, variante },
        });
        return response.data;
    } catch (error) {
        console.error(`❌ Error obteniendo los datos de la Carátula (${urlBase}) sin trámite:`, error);
        throw error;
    }
};

/**
 * Con `cuil` (se eligió un cliente) el backend además guarda en ese cliente lo que se completó
 * de sus datos propios: mail, fecha de nacimiento, claves y domicilio.
 */
export const generarCaratulaIndependiente = async (urlBase, datos, cuil) => {
    try {
        const response = await apiClient.post(`${API_URL}/${urlBase}/cliente`, datos, {
            params: { cuil: cuil || undefined },
            responseType: 'blob',
        });
        return response;
    } catch (error) {
        console.error(`❌ Error generando la Carátula (${urlBase}) sin trámite:`, error);
        throw error;
    }
};
